import React, { useEffect, useRef } from 'react';
import '../styles/Models.css';
import { useTranslation } from 'react-i18next';

const Models = () => {
  const modelsRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (modelsRef.current) {
      observer.observe(modelsRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div className="content-section models-section" ref={modelsRef}>
      <h2 className="models-title">{t('models.title.our')} <span className="gradient-text">{t('models.title.models')}</span></h2>
      
      <div className="models-intro">
        <p>
          {t('models.intro')}
        </p>
      </div>
      
      <div className="models-grid">
        <div className="model-card prag enhanced-model-card">
          <div className="model-card-glow prag-glow"></div>
          <div className="model-icon prag-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="model-header-enhanced">
            <h3 className="model-title-enhanced">{t('models.prag.title')}</h3>
            <div className="model-badge prag-badge">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="badge-text">Defensive</span>
            </div>
          </div>
          <p className="model-description">
            {t('models.prag.paragraph1')}
          </p>
          <div className="model-indicators">
            <div className="indicators-header">
              <span className="indicators-label">Technical Indicators:</span>
            </div>
            <div className="indicators-grid">
              <span className="indicator-tag">Volatility Bands</span>
              <span className="indicator-tag">Support/Resistance</span>
              <span className="indicator-tag">Momentum Oscillators</span>
              <span className="indicator-tag">Volume Analysis</span>
              <span className="indicator-tag">Risk Metrics</span>
              <span className="indicator-tag">Drawdown Protection</span>
            </div>
          </div>
          <div className="model-specs">
            <span className="spec">{t('models.specs.technology')}: {t('models.prag.technology')}</span>
            <span className="spec">{t('models.specs.function')}: {t('models.prag.function')}</span>
          </div>
        </div>
        
        <div className="model-card hyper enhanced-model-card">
          <div className="model-card-glow hyper-glow"></div>
          <div className="model-icon hyper-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="model-header-enhanced">
            <h3 className="model-title-enhanced">{t('models.hyper.title')}</h3>
            <div className="model-badge hyper-badge">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="badge-text">Growth</span>
            </div>
          </div>
          <p className="model-description">
            {t('models.hyper.paragraph1')}
          </p>
          <div className="model-indicators">
            <div className="indicators-header">
              <span className="indicators-label">Technical Indicators:</span>
            </div>
            <div className="indicators-grid">
              <span className="indicator-tag">Momentum Oscillators</span>
              <span className="indicator-tag">Breakout Detection</span>
              <span className="indicator-tag">Relative Strength</span>
              <span className="indicator-tag">Trend Confirmation</span>
              <span className="indicator-tag">Volume Expansion</span>
              <span className="indicator-tag">Multi-Timeframe</span>
            </div>
          </div>
          <div className="model-specs">
            <span className="spec">{t('models.specs.technology')}: {t('models.hyper.technology')}</span>
            <span className="spec">{t('models.specs.function')}: {t('models.hyper.function')}</span>
          </div>
        </div>
        
        <div className="model-card riemann enhanced-model-card">
          <div className="model-card-glow riemann-glow"></div>
          <div className="model-icon riemann-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 8L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 12L16 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="model-header-enhanced">
            <h3 className="model-title-enhanced">{t('models.riemann.title')}</h3>
            <div className="model-badge riemann-badge">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 6v6m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="badge-text">{t('models.riemann.systemType')}</span>
            </div>
          </div>
          <p className="model-description">
            {t('models.riemann.paragraph1')}
          </p>
          <div className="model-indicators">
            <div className="indicators-header">
              <span className="indicators-label">Core Features:</span>
            </div>
            <div className="indicators-grid">
              <span className="indicator-tag">Performance Tracking</span>
              <span className="indicator-tag">Credibility Scores</span>
              <span className="indicator-tag">Weighted Aggregation</span>
              <span className="indicator-tag">Analyst Ranking</span>
              <span className="indicator-tag">Sector Expertise</span>
              <span className="indicator-tag">Historical Accuracy</span>
            </div>
          </div>
          <div className="model-specs">
            <span className="spec">{t('models.specs.technology')}: {t('models.riemann.technology')}</span>
            <span className="spec">{t('models.specs.function')}: {t('models.riemann.function')}</span>
          </div>
        </div>
        
        <div className="model-card euler enhanced-model-card">
          <div className="model-card-glow euler-glow"></div>
          <div className="model-icon euler-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 18V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4.93 4.93L7.76 7.76" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16.24 16.24L19.07 19.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4.93 19.07L7.76 16.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="model-header-enhanced">
            <h3 className="model-title-enhanced">{t('models.euler.title')}</h3>
            <div className="model-badge euler-badge">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="badge-text">{t('models.euler.systemType')}</span>
            </div>
          </div>
          <p className="model-description">
            {t('models.euler.paragraph1')}
          </p>
          <div className="model-indicators">
            <div className="indicators-header">
              <span className="indicators-label">Technical Indicators:</span>
            </div>
            <div className="indicators-grid">
              <span className="indicator-tag">Volatility Surfaces</span>
              <span className="indicator-tag">Correlation Matrices</span>
              <span className="indicator-tag">Market Breadth</span>
              <span className="indicator-tag">Regime Detection</span>
              <span className="indicator-tag">Early Warning</span>
              <span className="indicator-tag">Risk Adjustment</span>
            </div>
          </div>
          <div className="model-specs">
            <span className="spec">{t('models.specs.technology')}: {t('models.euler.technology')}</span>
            <span className="spec">{t('models.specs.function')}: {t('models.euler.function')}</span>
          </div>
        </div>
        
        <div className="model-card tachyon enhanced-model-card">
          <div className="model-card-glow tachyon-glow"></div>
          <div className="model-icon tachyon-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="model-header-enhanced">
            <h3 className="model-title-enhanced">{t('models.tachyon.title')}</h3>
            <div className="model-badge tachyon-badge">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="12 6 12 12 16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="badge-text">{t('models.tachyon.systemType')}</span>
            </div>
          </div>
          <p className="model-description">
            {t('models.tachyon.paragraph1')}
          </p>
          <div className="model-indicators">
            <div className="indicators-header">
              <span className="indicators-label">Core Features:</span>
            </div>
            <div className="indicators-grid">
              <span className="indicator-tag">Cross-Market Analysis</span>
              <span className="indicator-tag">Price Discrepancies</span>
              <span className="indicator-tag">Execution Costs</span>
              <span className="indicator-tag">Slippage Modeling</span>
              <span className="indicator-tag">Latency Analysis</span>
              <span className="indicator-tag">Multi-Platform</span>
            </div>
          </div>
          <div className="model-specs">
            <span className="spec">{t('models.specs.technology')}: {t('models.tachyon.technology')}</span>
            <span className="spec">{t('models.specs.function')}: {t('models.tachyon.function')}</span>
          </div>
        </div>
        
        <div className="model-card aether enhanced-model-card">
          <div className="model-card-glow aether-glow"></div>
          <div className="model-icon aether-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 19C9 20.1046 7.65685 21 6 21C4.34315 21 3 20.1046 3 19C3 17.8954 4.34315 17 6 17C7.65685 17 9 17.8954 9 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 19C21 20.1046 19.6569 21 18 21C16.3431 21 15 20.1046 15 19C15 17.8954 16.3431 17 18 17C19.6569 17 21 17.8954 21 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 5C21 6.10457 19.6569 7 18 7C16.3431 7 15 6.10457 15 5C15 3.89543 16.3431 3 18 3C19.6569 3 21 3.89543 21 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 5C9 6.10457 7.65685 7 6 7C4.34315 7 3 6.10457 3 5C3 3.89543 4.34315 3 6 3C7.65685 3 9 3.89543 9 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 7C6 10.3137 9.13401 13 13 13C16.866 13 20 10.3137 20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="model-header-enhanced">
            <h3 className="model-title-enhanced">{t('models.aether.title')}</h3>
            <div className="model-badge aether-badge">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="badge-text">{t('models.aether.systemType')}</span>
            </div>
          </div>
          <p className="model-description">
            {t('models.aether.paragraph1')}
          </p>
          <div className="model-indicators">
            <div className="indicators-header">
              <span className="indicators-label">Core Features:</span>
            </div>
            <div className="indicators-grid">
              <span className="indicator-tag">VIX Monitoring</span>
              <span className="indicator-tag">Sentiment Analysis</span>
              <span className="indicator-tag">Macro Trends</span>
              <span className="indicator-tag">Sector Rotation</span>
              <span className="indicator-tag">Intermarket Analysis</span>
              <span className="indicator-tag">Market Breadth</span>
            </div>
          </div>
          <div className="model-specs">
            <span className="spec">{t('models.specs.technology')}: {t('models.aether.technology')}</span>
            <span className="spec">{t('models.specs.function')}: {t('models.aether.function')}</span>
          </div>
        </div>
        
        <div className="model-card sigma enhanced-model-card">
          <div className="model-card-glow sigma-glow"></div>
          <div className="model-icon sigma-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 7H20.5M12 12H20.5M12 17H20.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 19L4.514 13.414C4.18 12.898 4.18 12.229 4.514 11.714L8 6.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="model-header-enhanced">
            <h3 className="model-title-enhanced">{t('models.sigma.title')}</h3>
            <div className="model-badge sigma-badge">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <line x1="18" y1="20" x2="18" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="20" x2="12" y2="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="6" y1="20" x2="6" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="badge-text">{t('models.sigma.systemType')}</span>
            </div>
          </div>
          <p className="model-description">
            {t('models.sigma.paragraph1')}
          </p>
          <div className="model-indicators">
            <div className="indicators-header">
              <span className="indicators-label">Core Features:</span>
            </div>
            <div className="indicators-grid">
              <span className="indicator-tag">13F Analysis</span>
              <span className="indicator-tag">Performance Tracking</span>
              <span className="indicator-tag">Credibility Scores</span>
              <span className="indicator-tag">Weighted Aggregation</span>
              <span className="indicator-tag">Portfolio Changes</span>
              <span className="indicator-tag">Institutional Sentiment</span>
            </div>
          </div>
          <div className="model-specs">
            <span className="spec">{t('models.specs.technology')}: {t('models.sigma.technology')}</span>
            <span className="spec">{t('models.specs.function')}: {t('models.sigma.function')}</span>
          </div>
        </div>
        
        <div className="model-card baek enhanced-model-card">
          <div className="model-card-glow baek-glow"></div>
          <div className="model-icon baek-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.7519 11.1679L11.5547 9.03647C10.8901 8.59343 10 9.06982 10 9.86852V14.1315C10 14.9302 10.8901 15.4066 11.5547 14.9635L14.7519 12.8321C15.3457 12.4362 15.3457 11.5638 14.7519 11.1679Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="model-header-enhanced">
            <h3 className="model-title-enhanced">{t('models.baek.title')}</h3>
            <div className="model-badge baek-badge">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 1v6m0 6v6M23 12h-6M7 12H1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20.66 3.34l-4.24 4.24m0-8.48l4.24 4.24M3.34 20.66l4.24-4.24m0 8.48l-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="badge-text">{t('models.baek.systemType')}</span>
            </div>
          </div>
          <p className="model-description">
            {t('models.baek.paragraph1')}
          </p>
          <p className="model-description">
            {t('models.baek.paragraph2')}
          </p>
          <div className="model-indicators">
            <div className="indicators-header">
              <span className="indicators-label">Core Features:</span>
            </div>
            <div className="indicators-grid">
              <span className="indicator-tag">Dynamic Fine-Tuning</span>
              <span className="indicator-tag">Velocity Tracking</span>
              <span className="indicator-tag">Real-Time Adjustment</span>
              <span className="indicator-tag">Reinforcement Learning</span>
              <span className="indicator-tag">Meta-Learning</span>
              <span className="indicator-tag">System Coordination</span>
            </div>
          </div>
          <div className="model-specs">
            <span className="spec">{t('models.specs.technology')}: {t('models.baek.technology')}</span>
            <span className="spec">{t('models.specs.function')}: {t('models.baek.function')}</span>
          </div>
        </div>
        
        <div className="model-card val enhanced-model-card">
          <div className="model-card-glow val-glow"></div>
          <div className="model-icon val-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19 3L20 7L24 8L20 9L19 13L18 9L14 8L18 7L19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 15L6 19L10 20L6 21L5 25L4 21L0 20L4 19L5 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="model-header-enhanced">
            <h3 className="model-title-enhanced">{t('models.val.title')}</h3>
            <div className="model-badge val-badge">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <line x1="12" y1="1" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="badge-text">{t('models.val.systemType')}</span>
            </div>
          </div>
          <p className="model-description">
            {t('models.val.paragraph1')}
          </p>
          <p className="model-description">
            {t('models.val.paragraph2')}
          </p>
          <div className="model-indicators">
            <div className="indicators-header">
              <span className="indicators-label">Valuation Methods:</span>
            </div>
            <div className="indicators-grid">
              <span className="indicator-tag">DCF Analysis</span>
              <span className="indicator-tag">P/E Ratios</span>
              <span className="indicator-tag">Book Value</span>
              <span className="indicator-tag">Revenue Multiples</span>
              <span className="indicator-tag">Consensus Approach</span>
              <span className="indicator-tag">11 Strategies</span>
            </div>
          </div>
          <div className="model-specs">
            <span className="spec">{t('models.specs.technology')}: {t('models.val.technology')}</span>
            <span className="spec">{t('models.specs.function')}: {t('models.val.function')}</span>
          </div>
        </div>
      </div>
      
      <div className="ensemble-explanation">
        <h3>{t('models.ensemble.title')}</h3>
        <p>
          {t('models.ensemble.description')}
        </p>
      </div>
    </div>
  );
};

export default Models; 